import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mantine/core';
import { IconArrowBackUp, IconArrowForwardUp } from '@tabler/icons-react';
import Hls from 'hls.js';
import Plyr from 'plyr';
import 'plyr/dist/plyr.css';

const BunnyPlayer = ({ videoId, title }) => {
  const videoRef = useRef(null);
  const playerRef = useRef(null);
  const hlsRef = useRef(null);
  const tapRef = useRef({ time: 0, side: '' });
  
  const [seekIndicator, setSeekIndicator] = useState(null);
  const [seekAnimKey, setSeekAnimKey] = useState(0);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Initialize HLS for Bunny Stream
    if (Hls.isSupported()) {
      const hls = new Hls({ maxMaxBufferLength: 100 });
      hlsRef.current = hls;
      hls.loadSource(videoId);
      hls.attachMedia(video);
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = videoId;
    }

    // Initialize Plyr
    const isMobile = window.innerWidth <= 768;
    const player = new Plyr(video, {
      controls: isMobile 
        ? ['play-large', 'play', 'progress', 'current-time', 'duration', 'settings', 'fullscreen']
        : ['play-large', 'play', 'progress', 'current-time', 'duration', 'mute', 'volume', 'settings', 'fullscreen'],
      settings: ['quality', 'speed'],
    });
    
    playerRef.current = player;

    return () => {
      if (hlsRef.current) hlsRef.current.destroy();
      if (playerRef.current) playerRef.current.destroy();
    };
  }, [videoId]);

  // Double-tap to seek logic
  const handlePointerUp = (e) => {
    // Ignore if clicking on plyr controls
    if (e.target.closest('.plyr__controls')) return;

    const now = Date.now();
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const side = clickX < rect.width / 2 ? 'left' : 'right';
    const timeDiff = now - tapRef.current.time;

    if (timeDiff < 300 && tapRef.current.side === side) {
      // Double tap detected!
      setSeekIndicator(side);
      setSeekAnimKey(now);
      tapRef.current = { time: 0, side: '' };

      if (playerRef.current) {
        if (side === 'left') playerRef.current.rewind(10);
        else playerRef.current.forward(10);
      }

      setTimeout(() => setSeekIndicator(null), 800);
    } else {
      tapRef.current = { time: now, side };
    }
  };

  return (
    <Box 
      style={{ position: 'relative', width: '100%', aspectRatio: '16/9', background: '#000', borderRadius: '8px', overflow: 'hidden' }}
      onPointerUp={handlePointerUp}
      className="bunny-player-container"
    >
      <style jsx>{`
        .plyr { --plyr-color-main: #7243ec; }
        @keyframes seek-ripple {
          0% { opacity: 0; transform: scale(0.8); }
          20% { opacity: 1; transform: scale(1.1); }
          100% { opacity: 0; transform: scale(1); }
        }
        .animate-seek-ripple {
          animation: seek-ripple 0.8s ease-out forwards;
        }
      `}</style>

      {/* Double Tap Seek Animation Overlay */}
      {seekIndicator && (
        <div 
          key={seekAnimKey} 
          className="animate-seek-ripple"
          style={{
            position: 'absolute', top: 0, bottom: 0, width: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(0,0,0,0.4)', zIndex: 70, pointerEvents: 'none',
            ...(seekIndicator === 'left' 
                ? { left: 0, borderTopRightRadius: '50%', borderBottomRightRadius: '50%' } 
                : { right: 0, borderTopLeftRadius: '50%', borderBottomLeftRadius: '50%' })
          }}
        >
          {seekIndicator === 'left' 
            ? <IconArrowBackUp size={60} color="white" /> 
            : <IconArrowForwardUp size={60} color="white" />}
        </div>
      )}

      {/* Title Overlay */}
      <div style={{ position: 'absolute', top: '16px', left: '16px', color: 'white', fontWeight: 600, zIndex: 10, textShadow: '0 2px 4px rgba(0,0,0,0.8)', pointerEvents: 'none' }}>
        {title}
      </div>

      <video ref={videoRef} playsInline style={{ width: '100%', height: '100%' }} />
    </Box>
  );
};

export default BunnyPlayer;